/**
 * Sales hierarchy fields: the Lead/Event Rep, Territory Manager, Regional Manager and Sales Director dropdowns (with
 * the self-service rep option). Used by the event create wizard's Team step and, since 2026-10-29 (RM/TM role setup
 * REQ-8.3), by the event page's "Edit sales hierarchy" dialog, so both places edit the hierarchy the same way.
 */
import React from "react";
import {
  sortSalesPositionsByFirstName,
  displaySalesPositionName,
  LEAD_EVENT_REP_POSITION_TYPES,
  TERRITORY_MANAGER_POSITION_TYPES,
  REGIONAL_MANAGER_POSITION_TYPES,
  SALES_DIRECTOR_POSITION_TYPES,
} from "../../utils/salesPositionDisplay";
import {
  SELF_SERVICE_REP_VALUE,
  SELF_SERVICE_REP_LABEL,
  repSelectValue,
  parseRepSelectChange,
} from "../../utils/eventSelfService";
import {
  fieldManagerAllows,
  type FieldManagerPositionScope,
} from "../../utils/fieldManagerAccess";

export interface SalesPositionRow {
  id: string;
  title: string;
  assigned_name: string | null;
  position_type: string;
  parent_position_id: string | null;
}

export interface HierarchyChain {
  repId: string | null;
  tmId: string | null;
  rmId: string | null;
  sdId: string | null;
}

const SalesHierarchyFields: React.FC<{
  salesPositions: SalesPositionRow[];
  hierarchyChain: HierarchyChain;
  isSelfService: boolean;
  onChange: (next: HierarchyChain, isSelfService: boolean) => void;
  note?: React.ReactNode;
  /** Set for an RM / TM: limits the choices to what they may save. */
  fieldManagerScope?: FieldManagerPositionScope | null;
}> = ({
  salesPositions,
  hierarchyChain,
  isSelfService,
  onChange,
  note,
  fieldManagerScope,
}) => {
  const slots: {
    label: string;
    slot: keyof HierarchyChain;
    allowed: string[];
  }[] = [
    {
      label: "Lead/Event Rep",
      slot: "repId",
      allowed: LEAD_EVENT_REP_POSITION_TYPES,
    },
    {
      label: "Territory Manager",
      slot: "tmId",
      allowed: TERRITORY_MANAGER_POSITION_TYPES,
    },
    {
      label: "Regional Manager",
      slot: "rmId",
      allowed: REGIONAL_MANAGER_POSITION_TYPES,
    },
    {
      label: "Sales Director",
      slot: "sdId",
      allowed: SALES_DIRECTOR_POSITION_TYPES,
    },
  ];
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Sales Hierarchy
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {slots.map(({ label, slot, allowed }) => {
          const choices = sortSalesPositionsByFirstName(
            salesPositions.filter(
              (sp) =>
                allowed.includes(sp.position_type) &&
                (!fieldManagerScope ||
                  fieldManagerAllows(fieldManagerScope, slot, sp.id)),
            ),
          );
          const isRepSlot = slot === "repId";
          const currentValue = isRepSlot
            ? repSelectValue(hierarchyChain.repId, isSelfService)
            : (hierarchyChain[slot] ?? "");
          return (
            <div key={slot}>
              <label className="block text-xs text-gray-600 mb-1">
                {label}
              </label>
              <select
                value={currentValue}
                onChange={(e) => {
                  if (isRepSlot) {
                    const parsed = parseRepSelectChange(e.target.value);
                    onChange(
                      { ...hierarchyChain, repId: parsed.repId },
                      parsed.isSelfService,
                    );
                  } else {
                    onChange(
                      { ...hierarchyChain, [slot]: e.target.value || null },
                      isSelfService,
                    );
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="">Select…</option>
                {isRepSlot && (
                  <option value={SELF_SERVICE_REP_VALUE}>
                    {SELF_SERVICE_REP_LABEL}
                  </option>
                )}
                {currentValue &&
                  currentValue !== SELF_SERVICE_REP_VALUE &&
                  !choices.find((c) => c.id === currentValue) &&
                  (() => {
                    const sp = salesPositions.find(
                      (s) => s.id === currentValue,
                    );
                    return (
                      <option value={currentValue}>
                        {sp
                          ? displaySalesPositionName(sp)
                          : "(unknown position)"}
                      </option>
                    );
                  })()}
                {choices.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {displaySalesPositionName(sp)}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      {note}
      {fieldManagerScope && (
        <p className="mt-3 text-xs text-gray-600">
          Regional and Territory Managers staff reps from their own team; ask an
          admin to add a rep from another team.
        </p>
      )}
      <p className="mt-3 text-xs text-gray-500">
        The Lead/Event Rep comes from the partner account when you pick an
        organization; the Territory Manager, Regional Manager and Sales Director
        follow that rep&apos;s reporting chain. Override any field for matrixed
        events.
      </p>
    </div>
  );
};

export default SalesHierarchyFields;
